import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Home, CalendarDays, MessagesSquare, Trophy, Tent, Radio, User, Shield, LogOut, Users, Award, ShoppingBag, LayoutGrid, ListOrdered, UserSearch, Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ConnectionBanner } from "@/components/ConnectionBanner";
import { ScoreTicker } from "@/components/ScoreTicker";
import { NotificationBell } from "@/components/NotificationBell";
import { startSession, endSession } from "@/lib/telemetry";
import { MAIN_NAV } from "@/lib/mainNav";

// Icon per nav item, keyed by testid so the order/labels stay owned by mainNav.
const ICONS = {
  "nav-home": Home, "nav-games": CalendarDays, "nav-teams": LayoutGrid, "nav-players": UserSearch,
  "nav-standings": ListOrdered, "nav-rooms": MessagesSquare, "nav-predict": Trophy, "nav-leagues": Award,
  "nav-tailgates": Tent, "nav-live": Radio, "nav-connections": Users, "nav-shop": ShoppingBag, "nav-profile": User,
};
const NAV = MAIN_NAV.map((n) => ({ ...n, icon: ICONS[n.testid] || Home }));

function itemClass({ isActive }) {
  return [
    "group flex items-center gap-3 px-3 h-11 rounded-lg text-sm transition-colors",
    isActive ? "bg-[#24362A] text-white" : "text-white/60 hover:text-white hover:bg-white/5",
  ].join(" ");
}

export function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  // One authenticated web session per mounted shell. session_end fires on
  // unmount and on tab hide/close. Non-blocking, silent on failure.
  useEffect(() => {
    startSession();
    window.addEventListener("pagehide", endSession);
    return () => {
      window.removeEventListener("pagehide", endSession);
      endSession();
    };
  }, []);

  const nav = [...NAV];
  if (user?.is_admin) {
    nav.push({ to: "/moderation", label: "Moderation", icon: Shield, testid: "nav-moderation" });
    nav.push({ to: "/admin", label: "Operator", icon: Shield, testid: "nav-admin" });
  }

  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen flex bg-[#10151D]">
      {/* Left rail (desktop) */}
      <aside className="hidden md:flex flex-col w-[288px] shrink-0 border-r border-[#2A3544] bg-[#131a24] sticky top-0 h-screen" data-testid="sidebar">
        <div className="px-5 h-[96px] flex items-center gap-3.5 border-b border-[#2A3544]">
          <img src="/tt-mic-cropped.png" alt="Turf Talk" className="h-[72px] w-auto object-contain" data-testid="sidebar-brand-logo" />
          <div className="flex flex-col">
            <span className="font-display text-[32px] leading-none tracking-wide text-white whitespace-nowrap" data-testid="sidebar-brand-wordmark">TURF TALK</span>
            <span className="font-semibold text-xs tracking-[0.02em] text-white/85 whitespace-nowrap mt-1" data-testid="sidebar-brand-subline">by SportChant</span>
          </div>
          <NotificationBell className="ml-auto w-10 h-10" />
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto tt-scroll">
          {nav.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink key={n.to} to={n.to} end={n.end} className={itemClass} data-testid={n.testid}>
                <Icon size={18} strokeWidth={2} />
                <span>{n.label}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="p-3 border-t border-[#2A3544]">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 rounded-full bg-[#24362A] grid place-items-center text-white font-display">
              {(user?.handle || "?").slice(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm text-white truncate">@{user?.handle}</div>
              <div className="text-[11px] text-white/40 truncate">{user?.is_admin ? "Operator" : user?.is_demo ? "Demo account" : "Member"}</div>
            </div>
            <button onClick={doLogout} data-testid="logout-btn" aria-label="Log out" className="w-8 h-8 grid place-items-center rounded-lg text-white/60 hover:text-white hover:bg-white/5 transition-colors">
              <LogOut size={16} />
            </button>
          </div>
          <div className="px-2 pt-2.5 text-[10px] leading-tight text-white/30" data-testid="shell-attribution">A SportChant experience · Designed and built by Titan Haven Group</div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 h-14 flex items-center justify-between px-4 bg-[#131a24] border-b border-[#2A3544]">
        <div className="flex items-center gap-2">
          <button onClick={() => setMenuOpen(true)} aria-label="Open menu" data-testid="m-menu-open" className="text-white/70 -ml-1 p-1"><Menu size={22} /></button>
          <img src="/tt-mic-cropped.png" alt="Turf Talk" className="h-9 w-auto object-contain" />
          <div className="flex flex-col">
            <span className="font-display text-lg leading-none text-white">TURF TALK</span>
            <span className="font-semibold text-[10px] tracking-[0.02em] text-white/85">by SportChant</span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <NotificationBell className="w-9 h-9" testid="m-nav-notifications-bell" />
          <button onClick={doLogout} aria-label="Log out" className="text-white/60 w-9 h-9 grid place-items-center"><LogOut size={18} /></button>
        </div>
      </div>

      {/* Mobile full-nav drawer — exposes every item (incl. Players) that the
          5-slot bottom bar can't fit. */}
      {menuOpen && (
        <div className="md:hidden fixed inset-0 z-50" data-testid="m-nav-drawer">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMenuOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-[80%] max-w-[320px] bg-[#131a24] border-r border-[#2A3544] p-3 overflow-y-auto tt-scroll">
            <div className="flex items-center justify-between px-2 h-12">
              <span className="font-display text-lg text-white">Menu</span>
              <button onClick={() => setMenuOpen(false)} aria-label="Close menu" data-testid="m-menu-close" className="text-white/60 p-1"><X size={20} /></button>
            </div>
            <nav className="space-y-1 mt-1">
              {nav.map((n) => {
                const Icon = n.icon;
                return (
                  <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setMenuOpen(false)} className={itemClass} data-testid={`d-${n.testid}`}>
                    <Icon size={18} strokeWidth={2} />
                    <span>{n.label}</span>
                  </NavLink>
                );
              })}
            </nav>
          </div>
        </div>
      )}

      <main className="flex-1 min-w-0 pt-14 md:pt-0 pb-20 md:pb-0">
        {user?.is_admin && <ConnectionBanner />}
        <div className="md:sticky md:top-0 z-30"><ScoreTicker /></div>
        {children}
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 h-16 bg-[#131a24] border-t border-[#2A3544] flex items-center justify-around px-1">
        {nav.slice(0, 5).map((n) => {
          const Icon = n.icon;
          return (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`m-${n.testid}`}
              className={({ isActive }) => `flex flex-col items-center gap-0.5 text-[10px] ${isActive ? "text-[#4ea152]" : "text-white/50"}`}>
              <Icon size={20} />
              <span>{n.label}</span>
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}
